import { Slot } from '@radix-ui/react-slot'
import { type VariantProps, cva } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '../utils'
import { Icon, IconTypes, IconWeight } from './icon'

const buttonVariants = cva(
  'inline-flex select-none items-center justify-center font-medium ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 text-center whitespace-nowrap transition-transform active:scale-[.98]',
  {
    variants: {
      variant: {
        'default':
          'font-medium bg-primary border-primary border text-shadow-xs shadow-xs text-shadow-white/10 text-primary-foreground hover:bg-primary focus:bg-primary hover:text-primary-foreground focus:text-primary-foreground',
        'destructive':
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        'outline': 'border border-input bg-background hover:bg-accent/60 shadow-xs',
        'secondary':
          'border bg-linear-to-t from-accent/15 to-accent/0 border-input border-b-foreground/22 hover:bg-accent/60 shadow-xs',
        'ghost-active': 'text-celestial bg-sky-blue/20',
        'ghost':
          'data-[state=open]:bg-accent text-foreground hover:bg-accent group-hover/hover:bg-accent group-hover/hover:text-accent-foreground transition duration-80 ease-out hover:duration-0',
        'ghost-primary':
          'text-primary bg-primary/20 hover:bg-primary/30 group-hover/hover:bg-accent group-hover/hover:text-accent-foreground',
        'ghost-destructive':
          'text-destructive bg-destructive/20 hover:bg-destructive/30 group-hover/hover:bg-accent group-hover/hover:text-accent-foreground',
        'link': 'text-primary underline-offset-4 hover:underline',
        'lime':
          'shadow-hard bg-button-lime-background text-button-lime-foreground border border-button-lime-foreground hover:bg-lime-green/90',
      },
      size: {
        'default': 'h-10 px-4 py-2 text-base',
        'sm': 'h-9 px-3',
        'lg': 'h-12 px-4',
        'icon': 'h-10 w-10',
        'icon-sm': 'h-8 w-8',
      },
      rounded: {
        false: 'rounded-md',
        true: 'rounded-full',
      },
    },
    defaultVariants: {
      rounded: false,
      variant: 'default',
      size: 'default',
    },
  },
)

export type ButtonVariantsProps = VariantProps<typeof buttonVariants>

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, ButtonVariantsProps {
  asChild?: boolean
  loading?: boolean
  loadingLabel?: string
  success?: boolean
  successLabel?: string
  icon?: IconTypes
  iconWeight?: IconWeight
  iconClassName?: string
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      children,
      loading,
      icon,
      iconWeight,
      loadingLabel = 'Please wait',
      type = 'button',
      rounded = false,
      iconClassName,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, rounded }), className, {
          'bg-success text-success-foreground pr-6 disabled:opacity-100': props.success,
        })}
        ref={ref}
        type={type}
        {...props}
        disabled={loading || props.disabled || props.success}
      >
        {loading ? (
          <>
            <Icon name="arrows-clockwise" className="mr-2 h-4 w-4 animate-spin" />
            {loadingLabel}
          </>
        ) : (
          <div className="flex items-center gap-1">
            {props.success ? (
              <>
                <svg
                  className="mr-1 h-5 w-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    className="animation-check stroke-linecap-round stroke-linejoin-round stroke-white stroke-[3px]"
                    d="M4 12L9 17L20 6"
                  />
                </svg>

                {props.successLabel || 'Success'}
              </>
            ) : (
              <>
                {icon && (
                  <Icon
                    name={icon}
                    weight={iconWeight}
                    className={cn(
                      {
                        '-mt-px mr-1 -ml-1': size !== 'icon' && size !== 'icon-sm',
                      },
                      'h-4',
                      iconClassName,
                    )}
                  />
                )}
                {children}
              </>
            )}
          </div>
        )}
      </Comp>
    )
  },
)
Button.displayName = 'Button'

const MoreButton = React.forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => (
  <Button ref={ref} variant="outline" size="icon" {...props}>
    <Icon name="dots-three" className="h-4" />
  </Button>
))

export { Button, MoreButton, buttonVariants }
